import React, { useCallback, useMemo, useState } from "react";
import Modal from "components/modal";
import { Chip } from "components/chip/chip";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useModal } from "shared/hooks/use-modal";
import { useFieldArray, useForm } from "react-hook-form";
import { useTaskTypeRejectReason } from "shared/hooks/api/use-task-type-reject-reason";
import { TaskRow } from "./task-row";
import * as notify from "shared/notify";
import { MAX_TASKS_ROWS } from "../../lib/constants";
import { useFinalQaBulkReject } from "../../../../shared/hooks/api/use-final-qa-bulk-reject";
import "./task-reject.scss";

export const TasksReject = ({ isVOReject, selectedUpcs, onUnselectRows, onTaskCompleteRefresh }) => {
    const containsSampleUpc = selectedUpcs.some(({ isSampleUpc }) => isSampleUpc);
    const { mutateAsync: completeTasks } = useFinalQaBulkReject();
    const { open: openRejectModal, onOpen: handleOpenRejectModal, onClose: handleCloseRejectModal } = useModal();
    const { handleSubmit, control, formState, reset, trigger } = useForm({
        defaultValues: {
            rejectTasks: [{ task: "", reason: "", upcs: [] }],
        },
    });
    const { isDirty, errors } = formState;
    const { data: allRejectReasons } = useTaskTypeRejectReason(containsSampleUpc);
    const { fields, append, remove } = useFieldArray({
        control,
        name: "rejectTasks",
    });
    const [selectedTasks, setSelectedTasks] = useState(new Map());
    const MAX_TASKS = useMemo(() => fields.length === MAX_TASKS_ROWS, [fields.length]);
    const allowedTaskOptions = useMemo(() => {
        return allRejectReasons ? Object.keys(allRejectReasons) : [];
    }, [allRejectReasons]);

    const handleCloseModal = useCallback(async () => {
        if (isDirty) {
            const result = await notify.confirm("You have unsaved data. Are you sure you want to navigate away? Unsaved data will be lost!");

            if (result) {
                reset();
                setSelectedTasks(new Map());
                handleCloseRejectModal();
            }
        } else {
            handleCloseRejectModal();
        }
    }, [handleCloseRejectModal, isDirty, reset]);

    const handleTaskValidation = useCallback((task, id) => {
        setSelectedTasks((selectedTasksMap) => {
            if (task) {
                return new Map([...selectedTasksMap, [id, task]]);
            }

            selectedTasksMap.delete(id);

            return new Map(selectedTasksMap);
        });
    }, []);

    const onSubmit = async ({ rejectTasks }) => {
        try {
            await completeTasks({ rejectTasks, selectedUpcs });
            notify.success("Task(s) has been rejected");
            handleCloseRejectModal();
            onUnselectRows();
            onTaskCompleteRefresh();
        } catch (e) {
            notify.error("Task(s) reject has failed");
        }
    };

    const handleRemoveTask = useCallback(
        (id) => {
            remove(id);
            setSelectedTasks((selectedTasksMap) => {
                selectedTasksMap.delete(id);

                // recreate indexes every time item is deleted from the form
                return new Map(
                    [...selectedTasksMap].map(([, value], index) => {
                        return [index, value];
                    })
                );
            });
        },
        [remove]
    );

    const handleAddNewTask = () => {
        append({ task: "", reason: "", upcs: [] });
        trigger("rejectTasks");
    };

    return (
        <>
            <div className="flex border">
                <Chip
                    label="Reject Task(s)"
                    icon={faClose}
                    onClick={handleOpenRejectModal}
                    disabled={isVOReject}
                />
            </div>
            {openRejectModal && (
                <Modal
                    cssClass="fqa-reject-modal"
                    show={true}
                    onClose={handleCloseModal}
                >
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="p-20 h-separator">
                            <h3>Bulk Reject Task(s)</h3>
                            <p>Specify the task(s) you would like to reject the selected UPC(s) to, as well as the reason.</p>
                            <div className="task-reject-form-container">
                                {fields.map((formField, index) => {
                                    return (
                                        <TaskRow
                                            key={formField.id}
                                            index={index}
                                            control={control}
                                            selectedUpcs={selectedUpcs}
                                            error={errors.rejectTasks?.[index]}
                                            allRejectReasonOptions={allRejectReasons}
                                            allowedTaskOptions={allowedTaskOptions}
                                            selectedTasks={selectedTasks}
                                            onValidateSelection={handleTaskValidation}
                                            onRemoveTask={handleRemoveTask}
                                        />
                                    );
                                })}
                            </div>
                            <button
                                className="button -text"
                                onClick={handleAddNewTask}
                                disabled={MAX_TASKS}
                            >
                                + Add Task
                            </button>
                        </div>
                        <div className="button-group -right m-t-6 p-l-20 p-r-20">
                            <button
                                type="button"
                                className="button"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="button -primary"
                            >
                                Reject
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};
